import { useState, useEffect } from "react";
import type { ProgressLog, DbProgressLog } from "@/types/progressLog";
import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import {
	progressLogService,
	convertToAppProgressLog,
} from "@/services/progressLogService";

export const useProgress = (goalId?: string) => {
	const [progress, setProgress] = useState<ProgressLog[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);

	useEffect(() => {
		const fetchProgress = async () => {
			try {
				const data = await progressLogService.getProgressLogs(goalId);
				setProgress(data);
			} catch (err) {
				setError(err as Error);
			} finally {
				setLoading(false);
			}
		};

		fetchProgress();

		// リアルタイム更新のサブスクリプション
		const subscription = progressLogService.subscribeToChanges(
			(payload: RealtimePostgresChangesPayload<DbProgressLog>) => {
				if (payload.eventType === "INSERT") {
					setProgress((prev) => [
						convertToAppProgressLog(payload.new),
						...prev,
					]);
				} else if (payload.eventType === "UPDATE") {
					setProgress((prev) =>
						prev.map((p) =>
							p.id === payload.new.id
								? convertToAppProgressLog(payload.new)
								: p,
						),
					);
				} else if (payload.eventType === "DELETE") {
					setProgress((prev) => prev.filter((p) => p.id !== payload.old.id));
				}
			},
			goalId,
		);

		return () => {
			subscription.unsubscribe();
		};
	}, [goalId]);

	return {
		progress,
		loading,
		error,
		createProgressLog: progressLogService.createProgressLog,
		updateProgressLog: progressLogService.updateProgressLog,
		deleteProgressLog: progressLogService.deleteProgressLog,
	};
};
